import {MOON_350, TEAL_400} from '@wandb/weave/common/css/globals.styles';
import {
  ChildPanel,
  ChildPanelFullConfig,
} from '@wandb/weave/components/Panel2/ChildPanel';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {Spec as PanelExpressionSpec} from '@wandb/weave/components/Panel2/PanelExpression';
import {
  useRegisterDocument,
  useSelectedDocumentId,
} from '@wandb/weave/components/Panel2/PanelInteractContext';
import {useWeaveContext} from '@wandb/weave/context';
import {
  NotebookComputeGraphContextProvider,
  RemoteEcosystemComputeGraphContextProvider,
} from '@wandb/weave/contextProviders';
import {voidNode} from '@wandb/weave/core';
import * as _ from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Resizable, ResizeCallbackData} from 'react-resizable';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Message} from 'semantic-ui-react';
import {useReadOnly} from 'slate-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {RunQueryContext} from '../../state/runs/context';
import {useBetaFeatureWeavePythonEcosystem} from '../../util/useBetaFeature';
import {useWeaveInit} from '../panels.domain/weaveInit';
import {ReversibleWeaveErrorBoundary} from './ErrorBoundary';

export interface RootQueryPanelConfig {
  panelConfig: any;
  height?: number;
  width?: number;
  /**
   * If true, renders as ChildPanel with weave 1 execution context.
   * Expects `panelConfig` to be valid ChildPanel config.
   */
  isWeave1Panel?: boolean;
}

type RootQueryPanelProps = {
  fullHeight?: boolean;
  config: RootQueryPanelConfig;
  updateConfig: (newRootQueryConfig: Partial<RootQueryPanelConfig>) => void;
};

export const RootQueryPanel: React.FC<RootQueryPanelProps> = props => {
  const weaveEcosystemEnabled = useBetaFeatureWeavePythonEcosystem();
  // This is a temporary change to inform the EE that it is in a report
  // editing mode. We do this here, instead of in the EE, because the EE
  // is a generic Weave UI component and should not be aware of W&B context.
  const rq = useContext(RunQueryContext);
  const readOnlySlate = useReadOnly();
  const isEditingReport = rq?.report?.id != null && !readOnlySlate;
  // The following block should only ever be entered for panels from super early adopters of Weave
  // Python panels (before July 21, 2022) - and ONLY for internal employees. This can be safely removed
  // after a few weeks.
  if (props.config?.panelConfig?.__weaveBackendRequired__) {
    if (!isEditingReport) {
      return <></>;
    }
    if (weaveEcosystemEnabled) {
      return (
        <RemoteEcosystemComputeGraphContextProvider>
          <ConversionCTA exp={props.config?.panelConfig.exp} />
        </RemoteEcosystemComputeGraphContextProvider>
      );
    } else {
      // The following is displayed to users (possibly customers) who are viewing a report
      // which has a normal weave panel created with the weave-backend flag
      return (
        <Message
          info
          content="This panel is hidden because it uses beta features which are not currently enabled."
          size="mini"
        />
      );
    }
  }

  return (
    <ReversibleWeaveErrorBoundary
      {...props}
      innerComponent={
        props.config.isWeave1Panel
          ? RootQueryPanelChildPanel
          : RootQueryPanelPanelExpression
      }
      sourcePanel="RootQueryPanel"
      panelConfigKey="panelConfig"
    />
  );
};

const ConversionCTA: React.FC<{exp: any}> = ({exp}) => {
  const weave = useWeaveContext();
  const text = weave.expToString(exp);
  // The following is displayed only to those who have weave-backend enabled.
  return (
    <Message
      info
      content={`This is a normal Weave Panel which was configured with weave-backend. \
      To migrate, copy the following expression and paste it into a new Weave Python panel: ${text}`}
      size="mini"
    />
  );
};

const RootQueryPanelChildPanel: React.FC<RootQueryPanelProps> = ({
  config: rootQueryConfig,
  updateConfig: updateRootQueryConfig,
}) => {
  const [resizeSize, setResizeSize] = useState<
    ResizeCallbackData['size'] | null
  >(null);
  const height = resizeSize?.height ?? rootQueryConfig.height ?? 400;
  const width = resizeSize?.width ?? rootQueryConfig.width ?? 714;
  const updatePanelSize = useCallback(
    size => updateRootQueryConfig(size),
    [updateRootQueryConfig]
  );

  const childPanelConfig: ChildPanelFullConfig = rootQueryConfig.panelConfig;
  const updateChildPanelConfig = useCallback<any>(
    (newConfig: any) => {
      updateRootQueryConfig({panelConfig: {...childPanelConfig, ...newConfig}});
    },
    [childPanelConfig, updateRootQueryConfig]
  );
  const updateChildPanelConfig2 = useCallback(
    (change: <T>(oldConfig: T) => Partial<T>) => {
      updateRootQueryConfig({panelConfig: change(childPanelConfig)});
    },
    [childPanelConfig, updateRootQueryConfig]
  );

  const {register, unregister} = useRegisterDocument();
  const selectedDocumentId = useSelectedDocumentId();
  const ownDocumentId = childPanelConfig.config.documentId;
  const isSelectedPanel = ownDocumentId === selectedDocumentId;

  // Register panel for interaction
  useEffect(() => {
    register(ownDocumentId, {
      config: childPanelConfig,
      updateConfig: updateChildPanelConfig,
      updateConfig2: updateChildPanelConfig2,
    });
  }, [
    register,
    ownDocumentId,
    childPanelConfig,
    updateChildPanelConfig,
    updateChildPanelConfig2,
  ]);

  // Unregister panel on unmount
  useEffect(() => {
    return () => unregister(ownDocumentId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- need explicit empty deps; this effect is specifically for unmount

  return (
    <NotebookComputeGraphContextProvider>
      <Resizable
        height={height}
        width={width}
        onResize={(e, data) => setResizeSize(data.size)}
        onResizeStop={() => {
          // Must be in a timeout otherwise we hit an issue related to a race
          // condition inside an on layout hook inside of Slate
          setTimeout(() => {
            updatePanelSize(resizeSize);
            setResizeSize(null);
          }, 1);
        }}>
        <ChildPanelContainer
          height={height}
          width={width}
          isSelectedPanel={isSelectedPanel}>
          <ChildPanel
            controlBar="off"
            config={childPanelConfig}
            updateConfig={updateChildPanelConfig}
            updateConfig2={updateChildPanelConfig2}
          />
        </ChildPanelContainer>
      </Resizable>
    </NotebookComputeGraphContextProvider>
  );
};

const ChildPanelContainer = styled.div<{
  height: number;
  width: number;
  isSelectedPanel: boolean;
}>`
  height: ${props => props.height}px;
  width: ${props => props.width}px;
  max-width: 100%;
  margin: 0 auto;
  padding-top: 5px;
  border-radius: 6px;
  border: ${props =>
    props.isSelectedPanel ? `2px solid ${TEAL_400}` : `1px solid ${MOON_350}`};
  background: white;
`;

const RootQueryPanelPanelExpression: React.FC<RootQueryPanelProps> = props => {
  const {config, updateConfig, fullHeight} = props;

  useWeaveInit();
  const height = useMemo(() => config.height ?? 400, [config.height]);
  const [resizeHeight, setResizeHeight] = useState(height);
  const updateHeight = useCallback(
    (newHeight: number) => {
      updateConfig({height: newHeight});
    },
    [updateConfig]
  );
  // Stick path in to make dir panel work.
  // TODO: fix this to let dir panel update input instead of relying on context
  const [context, setContext] = useState<Panel2.PanelContext>({path: []});
  // TODO: any
  const updateContext = useCallback<Panel2.UpdateContext>(
    newContext => {
      setContext({...context, ...newContext});
    },
    [context]
  );

  const expressionConfig = useMemo(() => {
    if (_.isEmpty(config)) {
      return {autoFocus: true};
    }

    const oldConfig = config as any;
    if (config.panelConfig?.exp) {
      return config.panelConfig;
    } else {
      return {
        ..._.omit(config.panelConfig ?? {}, 'exp', 'panelConfig'),
        exp: oldConfig.exp,
        panelConfig: oldConfig.panelConfig,
      };
    }
  }, [config]);

  const updatePanelExpressionConfig = useCallback<any>(
    (newConfig: any) =>
      updateConfig({panelConfig: {...expressionConfig, ...newConfig}}),
    [updateConfig, expressionConfig]
  );

  const updatePanelExpressionConfig2 = useCallback(
    (change: <T>(oldConfig: T) => Partial<T>) =>
      updateConfig({panelConfig: change(expressionConfig)}),
    [expressionConfig, updateConfig]
  );

  const memoedInput = useMemo(() => {
    return voidNode();
  }, []);

  if (fullHeight) {
    return (
      <div style={{height: '100%'}}>
        <PanelExpressionSpec.Component
          input={memoedInput as any}
          loading={false}
          configMode={false}
          context={context}
          config={expressionConfig}
          updateConfig={updatePanelExpressionConfig}
          updateContext={updateContext}
          updateConfig2={updatePanelExpressionConfig2}
        />
      </div>
    );
  }

  return (
    <Resizable
      width={400}
      height={resizeHeight}
      onResize={(e, data) => {
        setResizeHeight(data.size.height);
      }}
      onResizeStop={() => updateHeight(resizeHeight)}>
      <div style={{height, padding: '6px'}}>
        <PanelExpressionSpec.Component
          input={memoedInput as any}
          loading={false}
          configMode={false}
          context={context}
          config={expressionConfig}
          updateConfig={updatePanelExpressionConfig}
          updateContext={updateContext}
          updateConfig2={updatePanelExpressionConfig2}
        />
      </div>
    </Resizable>
  );
};
